/**
 * useFeatureFlags — fetches the authenticated feature flags and gates UI on
 * them (cloud-migration wave 13, Req 7.6/7.7).
 *
 * Disabled features must be hidden from nav, routes, and components. If the
 * fetch fails we FAIL CLOSED: every feature is treated as disabled, so a
 * backend/network problem can never expose a gated feature.
 */
import { useQuery } from '@tanstack/react-query';
import { ALL_DISABLED, getFeatures, type FeatureFlags, type FeatureName } from '../api/features';

export function useFeatureFlags() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['features'],
    queryFn: getFeatures,
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

  // Fail closed: on error or before data arrives, all features are disabled.
  const flags: FeatureFlags = isError || !data ? ALL_DISABLED : data;

  const isEnabled = (name: FeatureName): boolean => flags[name] === true;

  return { flags, isEnabled, isLoading };
}
